import "./ExternalTransactions.scss";

import { Accordion, Col, Form, Row } from "react-bootstrap";
import { faAdd, faArrowsLeftRight, faEdit, faTrash } from "@fortawesome/free-solid-svg-icons";
import { formatDate, formatDateInEN, formatInReal } from "../../commons/util/HallAppUtil";
import { useCallback, useEffect, useMemo, useState } from "react";

import ActionPillButton from "../../commons/buttons/ActionPillButton";
import AppLoading from "../../commons/AppLoading";
import ConfirmModal from "../../commons/modal/ConfirmModal";
import EmptyState from "../../commons/EmptyState";
import ExternalTransactionAPI from "./services/ExternalTransactionAPI";
import ExternalTransactionFilter from "./ExternalTransactionFilter";
import FloatingButton from "../../commons/buttons/FloatingButton";
import FloatingVideoButton from "../../commons/buttons/FloatingVideoButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import HallAppToast from "../../commons/HallAppToast";
import InfiniteScroll from "react-infinite-scroll-component";
import MenuTitle from "../../commons/menu/MenuTitle";
import ModalConfirmSettlement from "./ModalConfirmSettlement";
import ModalExternalTransactions from "./ModalExternalTransaction";
import PullToRefresh from "react-simple-pull-to-refresh";
import ReportHeader from "../report-full/components/ReportHeader";
import ReportValues from "../report/components/ReportValues";
import { useLocation } from "react-router-dom";

/* eslint-disable react-hooks/exhaustive-deps */
const externalTransactionAPI = new ExternalTransactionAPI();

function ExternalTransactions() {
  const [showModal, setShowModal] = useState(false);
  const [showModalDelete, setShowModalDelete] = useState(false);

  const [transactions, setTransactions] = useState([]);
  const [isLoading, setLoading] = useState(true);
  const [totalResultRows, setTotalResultRows] = useState(0);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [isLoadingModalDelete, setIsLoadingModalDelete] = useState(false);

  const [summarySumValues, setSummarySumValues] = useState({});
  const [isLoadingSummary, setIsLoadingSummary] = useState(true);

  // ⚙️ controla “modo simples” vs “modo detalhado” do resumo
  // default: simples (menos poluição)
  const [showAdvancedSummary, setShowAdvancedSummary] = useState(false);

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const id = params.get("id");
  const statusQueryUrl = params.get("status");
  const startDateQueryUrl = params.get("startDate");
  const endDateQueryUrl = params.get("endDateDate");

  const [filter, setFilter] = useState({
    page: 0,
    limit: 50,
    type: statusQueryUrl || "",
    startDate: startDateQueryUrl || formatDateInEN(firstDayOfMonth),
    endDate: endDateQueryUrl || formatDateInEN(lastDayOfMonth),
  });

  // Modal confirmar pagamento/recebimento
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedSettlementTx, setSelectedSettlementTx] = useState(null);

  // Modal cancelar pagamento/recebimento
  const [showCancelPaymentModal, setShowCancelPaymentModal] = useState(false);
  const [selectedCancelTx, setSelectedCancelTx] = useState(null);
  const [isLoadingCancelPayment, setIsLoadingCancelPayment] = useState(false);

  function parseDate(dateStr) {
    if (!dateStr) return null;
    const [y, m, d] = String(dateStr).split("-");
    const dt = new Date(Number(y), Number(m) - 1, Number(d));
    dt.setHours(0, 0, 0, 0);
    return dt;
  }

  // ✅ sempre editar/excluir usando a "regra" (externalTransactionId), não a ocorrência
  function withRuleId(transaction) {
    const ruleId = transaction?.externalTransactionId ?? transaction?.id;
    return { ...transaction, _ruleId: ruleId };
  }

  function openConfirmSettlement(transaction) {
    setSelectedSettlementTx(withRuleId(transaction));
    setShowConfirmModal(true);
  }

  // ✅ confirmar pagamento/recebimento
  async function confirmSettlement(occurrenceId, payload) {
    try {
      await externalTransactionAPI.confirmSettlement(occurrenceId, payload);
      HallAppToast.success();
      setFilter((prev) => ({ ...prev, page: 0 }));
    } catch (_) {
      HallAppToast.genericError();
    }
  }

  // ✅ abrir modal de cancelamento quando toggle tentar desligar
  function openCancelPayment(transaction) {
    setSelectedCancelTx(transaction);
    setShowCancelPaymentModal(true);
  }

  // ✅ cancela pagamento/recebimento (deletePayment)
  async function executeCancelPayment() {
    setIsLoadingCancelPayment(true);
    try {
      await externalTransactionAPI.deletePayment(selectedCancelTx?.id);
      HallAppToast.success();

      setShowCancelPaymentModal(false);
      setSelectedCancelTx(null);

      setFilter((prev) => ({ ...prev, page: 0 }));
    } catch (_) {
      HallAppToast.genericError();
    } finally {
      setIsLoadingCancelPayment(false);
    }
  }

  const summarySum = useCallback(async () => {
    try {
      setIsLoadingSummary(true);
      const response = await externalTransactionAPI.summarySum(filter);
      setSummarySumValues(response.data);
    } catch (_) {
      setSummarySumValues({});
    } finally {
      setIsLoadingSummary(false);
    }
  }, [filter]);

  const findAllTransactions = useCallback(async () => {
    try {
      if (filter.page === 0) {
        setTransactions([]);
        setTotalResultRows(0);
      }

      await summarySum();

      const response = await externalTransactionAPI.advancedSearch(id, filter);

      if (filter.page === 0) setTransactions(response.data.content || []);
      else setTransactions((prev) => [...prev, ...(response.data.content || [])]);

      setTotalResultRows(response.data.totalResultRows || 0);
    } catch (_) {
      if (filter.page === 0) {
        setTransactions([]);
        setTotalResultRows(0);
      }
    } finally {
      setLoading(false);
    }
  }, [filter, summarySum, id]);

  useEffect(() => {
    findAllTransactions();
  }, [filter]);

  async function refresh() {
    setFilter((prev) => ({ ...prev, page: 0 }));
  }

  function openEdit(transaction) {
    setSelectedTransaction(withRuleId(transaction));
    setShowModal(true);
  }

  function openDelete(transaction) {
    setSelectedTransaction(withRuleId(transaction));
    setShowModalDelete(true);
  }

  async function executeDeletion() {
    setIsLoadingModalDelete(true);
    try {
      await externalTransactionAPI.delete(selectedTransaction?._ruleId);
      setShowModalDelete(false);
      HallAppToast.success();
      setFilter((prev) => ({ ...prev, page: 0 }));
    } catch (_) {
      HallAppToast.genericError();
    } finally {
      setIsLoadingModalDelete(false);
    }
  }

  function changeType(value) {
    setFilter((prev) => ({
      ...prev,
      page: 0,
      type: value === "" ? null : value,
    }));
  }

  function changeDates(startDate, endDate) {
    if (endDate === null) endDate = startDate;

    setFilter((prev) => ({
      ...prev,
      page: 0,
      startDate,
      endDate,
    }));
  }

  // ✅ Agrupamento:
  // OUTPUT: vencidas (sem payment), a vencer (sem payment), pagas (com paymentDate)
  // INPUT: a receber (sem paymentDate), recebidas (com paymentDate)
  const grouped = useMemo(() => {
    const overdue = [];
    const upcoming = [];
    const paid = [];

    const receivable = [];
    const received = [];

    (transactions || []).forEach((t) => {
      const hasPayment = !!t?.paymentDate;

      if (t?.type === "INPUT") {
        if (hasPayment) received.push(t);
        else receivable.push(t);
        return;
      }

      if (hasPayment) {
        paid.push(t);
        return;
      }

      const dt = parseDate(t?.transactionDate);
      if (!dt) {
        upcoming.push(t);
        return;
      }

      if (dt < today) overdue.push(t);
      else upcoming.push(t);
    });

    const byTxDate = (a, b) => (a.transactionDate || "").localeCompare(b.transactionDate || "");
    const byPayDate = (a, b) => (a.paymentDate || "").localeCompare(b.paymentDate || "");

    overdue.sort(byTxDate);
    upcoming.sort(byTxDate);
    paid.sort(byPayDate);

    receivable.sort(byTxDate);
    received.sort(byPayDate);

    const sum = (arr) => (arr || []).reduce((acc, t) => acc + (Number(t?.value) || 0), 0);

    return {
      overdue,
      upcoming,
      paid,
      receivable,
      received,
      meta: {
        overdueCount: overdue.length,
        overdueTotal: sum(overdue),

        upcomingCount: upcoming.length,
        upcomingTotal: sum(upcoming),

        paidCount: paid.length,
        paidTotal: sum(paid),

        receivableCount: receivable.length,
        receivableTotal: sum(receivable),

        receivedCount: received.length,
        receivedTotal: sum(received),
      },
    };
  }, [transactions, today]);

  // ✅ Resumo “simples” (não polui):
  // 1) A pagar (somando vencidas + a vencer)
  // 2) A receber
  // 3) Saldo
  const summaryUi = useMemo(() => {
    const payable = (grouped?.meta?.overdueTotal || 0) + (grouped?.meta?.upcomingTotal || 0);
    const receivable = grouped?.meta?.receivableTotal || 0;

    const payableCount = (grouped?.meta?.overdueCount || 0) + (grouped?.meta?.upcomingCount || 0);
    const receivableCount = grouped?.meta?.receivableCount || 0;

    const totalInput = Number(summarySumValues?.totalInput || 0);
    const totalOutput = Number(summarySumValues?.totalOutput || 0);
    const balance = totalInput - totalOutput;

    // avançado (parceladas) — só aparece quando usuário abrir o “detalhar”
    const installmentsOutCount = (transactions || []).filter(
      (t) => t?.type === "OUTPUT" && t?.recurrenceType === "INSTALLMENTS"
    ).length;

    const installmentsInCount = (transactions || []).filter(
      (t) => t?.type === "INPUT" && t?.recurrenceType === "INSTALLMENTS"
    ).length;

    return {
      payable,
      payableCount,
      receivable,
      receivableCount,
      balance,
      installmentsOutCount,
      installmentsInCount,
    };
  }, [grouped, summarySumValues, transactions]);

  function renderCard(transaction) {
    const isEntry = transaction.type === "INPUT";
    const hasPayment = !!transaction?.paymentDate;

    const isInstallments = transaction.recurrenceType === "INSTALLMENTS";
    const isFixed = transaction.recurrenceType === "FIXED";

    let recurrenceLabel = null;
    if (isFixed) recurrenceLabel = "Fixo mensal";
    if (isInstallments) {
      const total = transaction.installmentsTotal;
      const current = transaction.installmentIndex;

      if (current && total) recurrenceLabel = `Parcela ${current} de ${total}`;
      else if (total) recurrenceLabel = `Parcelado ${total}×`;
      else recurrenceLabel = "Parcelado";
    }

    const recurrenceClass = isInstallments ? "is-installments" : isFixed ? "is-fixed" : "";

    const dateLabel = isEntry ? "Data de entrada" : "Vencimento";
    const paymentLabel = isEntry ? "Recebido em" : "Pago em";

    return (
      <Col xs={12} sm={12} md={4} lg={4} key={transaction.id} style={{ marginBottom: 12 }}>
        <div className={`transaction-card ${isEntry ? "entry" : "output"} ${hasPayment ? "is-paid" : ""}`}>
          <div className="transaction-card__header">
            <div className="transaction-card__headerLeft">
              <span className={`transaction-card__type ${isEntry ? "entry" : "output"}`}>
                {isEntry ? "Entrada" : "Saída"}
              </span>

              {recurrenceLabel && (
                <span className={`transaction-card__recurrence ${recurrenceClass}`}>{recurrenceLabel}</span>
              )}

              {hasPayment && (
                <span className="transaction-card__paidBadge">{isEntry ? "RECEBIDO" : "PAGO"}</span>
              )}
            </div>

            <div className="transaction-card__toggle">
              <Form.Check
                type="switch"
                id={`pay-switch-${transaction.id}`}
                checked={hasPayment}
                onChange={() => {
                  if (hasPayment) openCancelPayment(transaction);
                  else openConfirmSettlement(transaction);
                }}
              />
            </div>
          </div>

          <div className={`transaction-card__value ${isEntry ? "entry" : "output"}`}>
            {formatInReal(transaction?.value)}
          </div>

          <div className="transaction-card__info">
            <div className="transaction-card__name">{transaction.name}</div>

            {!!transaction.description && (
              <div className="transaction-card__description">{transaction.description}</div>
            )}

            {hasPayment && (
              <div className="transaction-card__paymentInfo">
                <div>
                  <strong>{paymentLabel}:</strong> {formatDate(transaction.paymentDate)}
                </div>
                {!!transaction.paymentObservation && (
                  <div className="transaction-card__paymentObs">
                    <strong>Obs:</strong> {transaction.paymentObservation}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="transaction-card__date">
            <span className="transaction-card__dateLabel">{dateLabel}:</span>{" "}
            <span className="transaction-card__dateValue">{formatDate(transaction.transactionDate)}</span>
          </div>

          <div className="transaction-card__actionsRow">
            <ActionPillButton icon={faEdit} label="Editar" title="Editar lançamento" onClick={() => openEdit(transaction)} />
            <ActionPillButton
              icon={faTrash}
              label="Excluir"
              title="Excluir lançamento"
              onClick={() => openDelete(transaction)}
              color="#b91c1c"
              borderColor="#fecaca"
            />
          </div>
        </div>
      </Col>
    );
  }

  // ✅ Lista de seções (só renderiza as que têm itens)
  const sections = useMemo(() => {
    const s = [];

    if (grouped?.overdue?.length > 0) {
      s.push({
        key: "overdue",
        tone: "overdue",
        title: "Contas vencidas",
        count: grouped.meta.overdueCount,
        total: grouped.meta.overdueTotal,
        items: grouped.overdue,
      });
    }

    if (grouped?.upcoming?.length > 0) {
      s.push({
        key: "upcoming",
        tone: "upcoming",
        title: "Contas a vencer",
        count: grouped.meta.upcomingCount,
        total: grouped.meta.upcomingTotal,
        items: grouped.upcoming,
      });
    }

    if (grouped?.paid?.length > 0) {
      s.push({
        key: "paid",
        tone: "paid",
        title: "Contas pagas",
        count: grouped.meta.paidCount,
        total: grouped.meta.paidTotal,
        items: grouped.paid,
      });
    }

    if (grouped?.receivable?.length > 0) {
      s.push({
        key: "receivable",
        tone: "entry",
        title: "A receber",
        count: grouped.meta.receivableCount,
        total: grouped.meta.receivableTotal,
        items: grouped.receivable,
      });
    }

    if (grouped?.received?.length > 0) {
      s.push({
        key: "received",
        tone: "received",
        title: "Recebidas",
        count: grouped.meta.receivedCount,
        total: grouped.meta.receivedTotal,
        items: grouped.received,
      });
    }

    return s;
  }, [grouped]);

  const defaultAccordionKey = useMemo(() => {
    // abre a primeira seção “mais urgente” disponível
    return sections?.[0]?.key || "upcoming";
  }, [sections]);

  // ✅ UI de Resumo “menos poluída”:
  // Mostra 3 cards focados em decisão (A pagar, A receber, Saldo)
  // E um “Detalhar” pequeno (toggle), que abre 2 informações extra de parceladas
  function renderSummary() {
    // Se ficar pesado/estranho com pouca tela, desliga aqui e volta pro bloco antigo
    return (
      <div style={{ marginTop: "60px" }}>
        <Row className="mb-2">
          <Col xs={12} md={4}>
            <ReportValues
              isLoadingSummary={isLoadingSummary}
              bgColor="#eb214e"
              value={formatInReal(summaryUi.payable)}
              label={`a pagar (${summaryUi.payableCount})`}
            />
          </Col>

          <Col xs={12} md={4}>
            <ReportValues
              isLoadingSummary={isLoadingSummary}
              bgColor="#28a745"
              value={formatInReal(summaryUi.receivable)}
              label={`a receber (${summaryUi.receivableCount})`}
            />
          </Col>

          <Col xs={12} md={4}>
            <ReportValues
              isLoadingSummary={isLoadingSummary}
              bgColor="#17a2b8"
              value={formatInReal(summaryUi.balance) || 0}
              label="saldo (período)"
            />
          </Col>
        </Row>

        <div className="summary-advanced-toggle">
          <button
            type="button"
            className="summary-advanced-toggle__btn"
            onClick={() => setShowAdvancedSummary((v) => !v)}
          >
            {showAdvancedSummary ? "Ocultar detalhes" : "Ver detalhes"}
          </button>
        </div>

        {showAdvancedSummary && (
          <Row className="mt-2">
            <Col xs={12} md={6}>
              <ReportValues
                isLoadingSummary={isLoadingSummary}
                bgColor="#6b7280"
                value={summaryUi.installmentsOutCount || 0}
                label="saídas parceladas (qtd)"
              />
            </Col>

            <Col xs={12} md={6}>
              <ReportValues
                isLoadingSummary={isLoadingSummary}
                bgColor="#6b7280"
                value={summaryUi.installmentsInCount || 0}
                label="entradas parceladas (qtd)"
              />
            </Col>
          </Row>
        )}
      </div>
    );
  }

  return (
    <div>
      {(id || statusQueryUrl) && <ReportHeader name="Transações Externa" />}

      {/* ✅ filtro permanece */}
      <div
        style={{
          position: "fixed",
          top: 60,
          left: 0,
          right: 0,
          zIndex: 9999,
          marginTop: "0",
          paddingTop: "0",
          display: showModal || showConfirmModal ? "none" : "block",
          backgroundColor: "white",
        }}
        className="filter-my-command"
      >
        <ExternalTransactionFilter changeDates={changeDates} changeStatus={changeType} />
      </div>

      <div>
        <PullToRefresh onRefresh={findAllTransactions}>
          <div
            className="container"
            style={{
              marginTop: 60,
              paddingBottom: "calc(var(--bottom-nav-h, 64px) + 90px + env(safe-area-inset-bottom))",
            }}
          >
            <MenuTitle title="Entradas e Saidas" />

            {/* ✅ resumo “menos poluído” (com opção de detalhes) */}
            {renderSummary()}

            <hr />

            {isLoading ? (
              <AppLoading />
            ) : (
              <PullToRefresh onRefresh={refresh}>
                <InfiniteScroll
                  dataLength={transactions.length}
                  key={transactions.length}
                  next={() => {
                    if (transactions.length < totalResultRows) {
                      setFilter((prev) => ({ ...prev, page: prev.page + 1 }));
                    }
                  }}
                  hasMore={transactions.length < totalResultRows}
                  loader={transactions.length < totalResultRows && <AppLoading />}
                  endMessage={
                    <p style={{ textAlign: "center" }}>
                      <b>Yay! Você já viu de tudo</b>
                    </p>
                  }
                >
                  {transactions.length === 0 ? (
                    <EmptyState
                      title="Nenhum lançamento externo"
                      description="Registre despesas (ex.: luz, água, compras) e receitas externas (ex.: aluguel de sala). Esses lançamentos ajudam a manter o controle do fluxo de caixa."
                      icon={<FontAwesomeIcon icon={faArrowsLeftRight} />}
                      actionLabel="Novo lançamento"
                      onAction={() => setShowModal(true)}
                    />
                  ) : (
                    <>
                      {sections.length === 0 ? (
                        <div className="finance-empty">Nenhum item encontrado neste período.</div>
                      ) : (
                        <Accordion defaultActiveKey={defaultAccordionKey} alwaysOpen className="finance-accordion">
                          {sections.map((sec) => (
                            <Accordion.Item key={sec.key} eventKey={sec.key}>
                              <Accordion.Header>
                                <div className={`finance-accordion__headerRow ${sec.tone}`}>
                                  <span>{sec.title}</span>
                                  <span className="finance-accordion__headerRight">
                                    {sec.count} • {formatInReal(sec.total)}
                                  </span>
                                </div>
                              </Accordion.Header>

                              <Accordion.Body>
                                <Row style={{ margin: 0, width: "100%" }}>
                                  {sec.items.map(renderCard)}
                                </Row>
                              </Accordion.Body>
                            </Accordion.Item>
                          ))}
                        </Accordion>
                      )}
                    </>
                  )}
                </InfiniteScroll>
              </PullToRefresh>
            )}

            <FloatingVideoButton youtubeUrl={"https://www.youtube.com/embed/4oESRFhAqLM"} />
            <FloatingButton icon={faAdd} onClick={() => setShowModal(true)} />

            {showModal && (
              <ModalExternalTransactions
                show={showModal}
                callTransactions={findAllTransactions}
                transactionToEdit={selectedTransaction}
                closeShow={() => {
                  setShowModal(false);
                  setSelectedTransaction(null);
                }}
              />
            )}

            {showConfirmModal && (
              <ModalConfirmSettlement
                show={showConfirmModal}
                transaction={selectedSettlementTx}
                closeShow={() => {
                  setShowConfirmModal(false);
                  setSelectedSettlementTx(null);
                }}
                onConfirm={confirmSettlement}
              />
            )}

            {/* ✅ excluir lançamento */}
            <ConfirmModal
              show={showModalDelete}
              title="Excluir"
              isLoading={isLoadingModalDelete}
              confirm={executeDeletion}
              closeModal={() => setShowModalDelete(false)}
            >
              Deseja remover a transação <span style={{ fontWeight: "bold" }}>{selectedTransaction?.name} </span>
              no valor de <span style={{ fontWeight: "bold" }}>{formatInReal(selectedTransaction?.value)}</span>?
            </ConfirmModal>

            {/* ✅ cancelar pagamento/recebimento */}
            <ConfirmModal
              show={showCancelPaymentModal}
              title={selectedCancelTx?.type === "INPUT" ? "Cancelar recebimento" : "Cancelar pagamento"}
              isLoading={isLoadingCancelPayment}
              confirm={executeCancelPayment}
              closeModal={() => {
                setShowCancelPaymentModal(false);
                setSelectedCancelTx(null);
              }}
            >
              Deseja cancelar{" "}
              <span style={{ fontWeight: "bold" }}>
                {selectedCancelTx?.type === "INPUT" ? "o recebimento" : "o pagamento"}
              </span>{" "}
              deste lançamento?
              {selectedCancelTx?.paymentDate && (
                <>
                  <br />
                  <span className="text-muted">
                    Confirmado em: <strong>{formatDate(selectedCancelTx.paymentDate)}</strong>
                  </span>
                </>
              )}
            </ConfirmModal>
          </div>
        </PullToRefresh>
      </div>
    </div>
  );
}

export default ExternalTransactions;
